import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { FaExclamationTriangle, FaRedo, FaNetworkWired, FaSync, FaServer, FaShieldAlt, FaSearch, FaEye, FaBan, FaPlug, FaKey, FaInfoCircle, FaCog } from 'react-icons/fa';
import '../../../styles/NetworkSecurityStyles.scss';
import * as Sentry from '@sentry/react';

/**
 * Network Security Dashboard - MikroTik Router Security Monitoring
 * Displays aggregated network security metrics from all MikroTik routers
 * Uses router monitoring system for automatic router inventory
 */
const NetworkSecurityDashboard = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [routers, setRouters] = useState([]);
    const [aggregatedSecurity, setAggregatedSecurity] = useState(null);
    const [selectedRouter, setSelectedRouter] = useState(null);
    const [routerSecurity, setRouterSecurity] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    // Helper function to get auth headers
    const getAuthHeaders = () => {
        const storedUser = user || JSON.parse(localStorage.getItem('user'));
        return storedUser?.token ? { Authorization: `Bearer ${storedUser.token}` } : {};
    };

    /**
     * Load routers from router monitoring system
     */
    const loadRouters = async () => {
        try {
            const response = await fetch('/api/NetworkSecurity/routers/discover', { headers: getAuthHeaders() });
            if (!response.ok) throw new Error('Failed to discover routers');
            const data = await response.json();
            setRouters(data);
            return data;
        } catch (err) {
            Sentry.captureException(err);
            console.error('Error loading routers:', err);
            throw err;
        }
    };

    /**
     * Load aggregated network security metrics
     */
    const loadAggregatedSecurity = async () => {
        try {
            const response = await fetch('/api/NetworkSecurity/security/aggregated', { headers: getAuthHeaders() });
            if (!response.ok) throw new Error('Failed to load security metrics');
            const data = await response.json();
            setAggregatedSecurity(data);
        } catch (err) {
            Sentry.captureException(err);
            console.error('Error loading security metrics:', err);
            throw err;
        }
    };

    /**
     * Load security data for specific router
     */
    const loadRouterSecurity = async (routerId) => {
        try {
            const response = await fetch(`/api/NetworkSecurity/security/router/${routerId}`, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error('Failed to load router security');
            const data = await response.json();
            setRouterSecurity(data);
        } catch (err) {
            Sentry.captureException(err);
            console.error('Error loading router security:', err);
            setRouterSecurity(null);
        }
    };

    /**
     * Initial data load
     */
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                await loadRouters();
                await loadAggregatedSecurity();
                setError(null);
            } catch (err) {
                Sentry.captureException(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);

    /**
     * Refresh all data
     */
    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            await loadRouters();
            await loadAggregatedSecurity();
            if (selectedRouter) {
                await loadRouterSecurity(selectedRouter.routerId);
            }
            setError(null);
        } catch (catchError) {
            Sentry.captureException(catchError);
            setError('Failed to refresh data');
        } finally {
            setRefreshing(false);
        }
    };

    /**
     * Handle router selection
     */
    const handleRouterSelect = async (router) => {
        setSelectedRouter(router);
        await loadRouterSecurity(router.routerId);
    };

    if (loading) {
        return (
            <div className="network-security-dashboard">
                <div className="loading">
                    <div className="spinner"></div>
                    <p>Loading network security data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="network-security-dashboard">
                <div className="error-message">
                    <FaExclamationTriangle />
                    <p>{error}</p>
                    <button onClick={handleRefresh} className="btn-retry">
                        <FaRedo /> Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="network-security-dashboard">
            <div className="dashboard-header">
                <h1>
                    <FaNetworkWired /> Network Security Monitoring
                </h1>
                <p className="subtitle">MikroTik Router Security Analytics</p>
                <button 
                    onClick={handleRefresh} 
                    className="btn-refresh"
                    disabled={refreshing}
                >
                    <FaSync className={refreshing ? 'spinning' : ''} />
                    {refreshing ? ' Refreshing...' : ' Refresh'}
                </button>
            </div>

            {/* Aggregated Security Summary */}
            {aggregatedSecurity && (
                <div className="security-summary">
                    <h2>Network-Wide Security Overview</h2>
                    <div className="summary-cards">
                        <div className="summary-card">
                            {/* FaServer used as fallback since FaRouter is not available in react-icons/fa */}
                            <div className="card-icon"><FaServer /></div>
                            <div className="card-content">
                                <div className="card-value">{aggregatedSecurity.totalRouters}</div>
                                <div className="card-label">Total Routers</div>
                            </div>
                        </div>
                        <div className="summary-card high-risk">
                            <div className="card-icon"><FaExclamationTriangle /></div>
                            <div className="card-content">
                                <div className="card-value">{aggregatedSecurity.highRiskRouters}</div>
                                <div className="card-label">High Risk Routers</div>
                            </div>
                        </div>
                        <div className="summary-card">
                            <div className="card-icon"><FaShieldAlt /></div>
                            <div className="card-content">
                                <div className="card-value">{aggregatedSecurity.totalFirewallEvents}</div>
                                <div className="card-label">Firewall Events</div>
                            </div>
                        </div>
                        <div className="summary-card warning">
                            <div className="card-icon"><FaSearch /></div>
                            <div className="card-content">
                                <div className="card-value">{aggregatedSecurity.portScansDetected}</div>
                                <div className="card-label">Port Scans Detected</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Router List */}
            <div className="routers-section">
                <h2>MikroTik Routers ({routers.length})</h2>
                <div className="routers-grid">
                    {routers.map((router) => (
                        <div 
                            key={router.routerId}
                            className={`router-card ${selectedRouter?.routerId === router.routerId ? 'selected' : ''}`}
                            onClick={() => handleRouterSelect(router)}
                        >
                            <div className="router-header">
                                <h3>{router.hostname}</h3>
                                <span className="router-ip">{router.ipAddress}</span>
                            </div>
                            <div className="router-details">
                                <div className="detail-item">
                                    <span className="label">RouterOS:</span>
                                    <span className="value">{router.routerOsVersion || 'Unknown'}</span>
                                </div>
                                <div className="detail-item">
                                    <span className="label">Site:</span>
                                    <span className="value">{router.site || 'N/A'}</span>
                                </div>
                            </div>
                            <div className="router-actions">
                                <button className="btn-view">
                                    <FaEye /> View Details
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {routers.length === 0 && (
                    <div className="no-data">
                        {/* FaServer used as fallback since FaRouter is not available in react-icons/fa */}
                        <FaServer />
                        <p>No MikroTik routers discovered</p>
                        <small>Configure router credentials in Settings &gt; Router Configuration</small>
                    </div>
                )}
            </div>

            {/* Selected Router Security Details */}
            {selectedRouter && routerSecurity && (
                <div className="router-security-details">
                    <h2>Security Details: {selectedRouter.hostname}</h2>
                    <div className="security-metrics">
                        <div className="metric-card">
                            <h3><FaBan /> Firewall Events</h3>
                            <div className="metric-value">{routerSecurity.firewallEventsCount}</div>
                            <div className="metric-subtitle">Dropped/Blocked Packets</div>
                        </div>
                        <div className="metric-card">
                            <h3><FaSearch /> Port Scans</h3>
                            <div className="metric-value">{routerSecurity.portScansCount}</div>
                            <div className="metric-subtitle">Detected Scans</div>
                        </div>
                        <div className="metric-card">
                            <h3><FaPlug /> Unusual Connections</h3>
                            <div className="metric-value">{routerSecurity.unusualConnectionsCount}</div>
                            <div className="metric-subtitle">Anomalous Activity</div>
                        </div>
                        <div className="metric-card">
                            <h3><FaKey /> VPN Access</h3>
                            <div className="metric-value">{routerSecurity.vpnAccessLogsCount}</div>
                            <div className="metric-subtitle">VPN Connections</div>
                        </div>
                    </div>

                    {routerSecurity.riskScore && (
                        <div className="risk-assessment">
                            <h3>Risk Assessment</h3>
                            <div className={`risk-score risk-${routerSecurity.riskLevel?.toLowerCase()}`}>
                                <div className="score-value">{routerSecurity.riskScore}/100</div>
                                <div className="score-label">{routerSecurity.riskLevel} Risk</div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Help Section */}
            <div className="help-section">
                <h3><FaInfoCircle /> About Network Security Monitoring</h3>
                <p>
                    This dashboard provides real-time network security monitoring across all MikroTik routers 
                    in the router monitoring system. Monitor firewall events, detect port scans, identify 
                    unusual connection patterns, and track VPN access logs for comprehensive network visibility.
                </p>
                <div className="help-links">
                    <a href="/settings/router-config" className="help-link">
                        <FaCog /> Configure Router Credentials
                    </a>
                    <a href="/dashboards/threat-hunting" className="help-link">
                        <FaShieldAlt /> View Threat Hunting Report
                    </a>
                </div>
            </div>
        </div>
    );
};

export default NetworkSecurityDashboard;
